import { Employee } from '../types/hr';

/**
 * Page keys that can be granted on an employee record.
 * Must stay in sync with routes in App.tsx and the sidebar in DashboardWrapper.
 */
export const ALL_PAGE_PERMISSIONS: { key: string; label: string }[] = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'analytics', label: 'Analytics' },
    { key: 'reporting-analytics', label: 'Reporting Analytics' },
    { key: 'user-analytics', label: 'User Analytics' },
    { key: 'users', label: 'Users' },
    { key: 'coaches', label: 'Coaches' },
    { key: 'crm', label: 'CRM' },
    { key: 'wizard', label: 'The Wizard' },
    { key: 'wellness-qa', label: 'Wellness Q&As' },
    { key: 'session-lms', label: 'Session Load Modifiers' },
    { key: 'training-types-sports', label: 'Training Types & Sports' },
    { key: 'global-activities', label: 'Global Activities' },
    { key: 'alert-rules', label: 'Alert Rules' },
    { key: 'push-notifications', label: 'Push Notifications' },
    { key: 'campaigns', label: 'Campaigns' },
    { key: 'reporting', label: 'Reporting' },
    { key: 'social-monitoring', label: 'Social Monitoring' },
    { key: 'cancellation-feedback', label: 'Cancellation Feedback' },
    { key: 'deletion-requests', label: 'Cancellation Requests' },
    { key: 'updates', label: 'App Updates' },
    { key: 'feedback', label: 'Feedback' },
    { key: 'support-docs', label: 'Internal Support Docs' },
    { key: 'background-gallery', label: 'Background Gallery' },
    { key: 'peak-global-settings', label: 'Peak Global Settings' },
    { key: 'admins', label: 'Admins' },
    { key: 'commercial/requests', label: 'Onboarding Requests' },
    { key: 'commercial/organisations', label: 'Organisations' },
    { key: 'hr/employees', label: 'HR — Employees' },
];

/** Signed-in staff may open these without a dedicated page grant. */
const PATHS_FOR_ANY_PANEL_EMPLOYEE = new Set(['profile', 'oauth/authorize']);

export interface PanelAccess {
    status?: string;
    enabled?: boolean;
    permissions?: string[];
    superAdmin?: boolean;
}

/**
 * Active panel employee.
 *
 * `enabled` is treated as granted when the field is omitted. createEmployee
 * does not write it, and the HR screen already treats omission as enabled
 * (`enabled !== false`). Requiring the literal boolean `true` would lock out
 * every existing staff account. An explicit `enabled: false` is denied.
 * A missing `status` is treated as active for the same legacy reason;
 * `inactive` and `terminated` are denied.
 *
 * Document existence is not enough: the record must also be a super admin
 * or hold at least one page permission.
 */
export function isActivePanelEmployee(employee: PanelAccess | null | undefined): boolean {
    if (!employee) return false;
    if (employee.enabled === false) return false;
    if (employee.status && employee.status !== 'active') return false;
    if (employee.superAdmin === true) return true;
    return Array.isArray(employee.permissions) && employee.permissions.length > 0;
}

export function permissionKeyForPath(pathname: string): string | null {
    const trimmed = pathname.split('?')[0].replace(/^\//, '').replace(/\/$/, '');
    if (!trimmed) return null;
    if (trimmed.startsWith('commercial/organisations/')) return 'commercial/organisations';
    return trimmed;
}

export function employeeCanAccessPath(employee: PanelAccess | null | undefined, pathname: string): boolean {
    if (!isActivePanelEmployee(employee)) return false;
    if (employee?.superAdmin === true) return true;
    const key = permissionKeyForPath(pathname);
    if (!key) return false;
    if (PATHS_FOR_ANY_PANEL_EMPLOYEE.has(key)) return true;
    const permissions = employee?.permissions;
    return Array.isArray(permissions) && permissions.includes(key);
}

export function firstAllowedPath(employee: PanelAccess | null | undefined): string | null {
    if (!isActivePanelEmployee(employee)) return null;
    if (employee?.superAdmin === true || employee?.permissions?.includes('dashboard')) {
        return '/dashboard';
    }
    const key = employee?.permissions?.find((permission) =>
        ALL_PAGE_PERMISSIONS.some((page) => page.key === permission)
    );
    return key ? `/${key}` : null;
}

export function panelAccessFromEmployee(employee: Employee | null | undefined): PanelAccess | null {
    if (!employee) return null;
    return {
        status: employee.status,
        enabled: employee.enabled,
        permissions: employee.permissions,
        superAdmin: employee.superAdmin,
    };
}
